import { createBrowserHistory } from 'history';
import { createSearchParams, generatePath, matchPath } from 'react-router';

import { isBillingError, isUnpaid } from '@/utils/billing';
import { isImporting, isInvalid } from '@/utils/shops';
import { getPublishStatus } from '@/utils/listings/statuses';
import { isProductEdited } from '@/utils/product/isProductEdited';
import { isValidNumber } from '@/utils/number';
import { getSize } from '@/utils/iterable/getSize';
import { store } from '@/store';

import { PAGES, PAGE_TO_ROUTE_MAP, PARAMS, ROUTES } from '@/constants/routes';
import { PUBLISH_ACTION } from '@/constants/listings';
import { PROFILE_TYPE } from '@/constants/profiles';
import { STATUSES } from '@/constants/shops';
import { FEATURE } from '@/constants/features';
import { MODALS } from '@/constants/modal';
import { EMPTY } from '@/constants';
import { PLAN } from '@/constants/billing';

import actions from '@/actions';

export const history = createBrowserHistory();

export function navigateTo(route, external = false) {
  if (external) {
    window.location = route;
  } else {
    history.push(route);
  }
}

export function goBack(fallback = ROUTES.ROOT) {
  if (history.location.key === 'default') {
    history.replace(fallback);
  } else {
    history.back();
  }
}

export function openNewTab(route) {
  window.open(route, '_blank', 'noopener=true,noreferrer=true');
}

export function navigation({ dispatch = store.dispatch, state } = {}) {
  function getState() {
    return state || store.getState();
  }

  function getPage() {
    const { location: { pathname }} = history;

    for (const page in PAGE_TO_ROUTE_MAP) {
      if (PAGE_TO_ROUTE_MAP.hasOwnProperty(page)) {
        const path = PAGE_TO_ROUTE_MAP[page];

        if (matchPath({ path, end: true }, pathname)) {
          return page;
        }
      }
    }

    return undefined;
  }

  const PAGE = getPage();

  function makeRoute({ page, params = {}, path, search = {}}) {
    switch (page) {
      case PAGES.BILLING: {
        if (search[PARAMS.SHOP_ID]) {
          if (!isValidNumber(search[PARAMS.SHOP_ID]) || !getState().hasIn(['shops', 'byId', search[PARAMS.SHOP_ID]])) {
            const current = getState().getIn(['shops', 'current']);

            if (current) {
              search[PARAMS.SHOP_ID] = getState().getIn(['shops', 'current']);
            }
          }
        }

        break;
      }

      case PAGES.BULK_EDIT:
      case PAGES.EDIT:
      case PAGES.LISTINGS:
      case PAGES.PROFILES:
      case PAGES.SCHEDULE:
      case PAGES.SCHEDULE_LISTINGS:
      case PAGES.SCHEDULE_UPDATES:
      case PAGES.STUDIO:
      case PAGES.STUDIO_PHOTOS:
      case PAGES.STUDIO_VIDEOS: {
        if (!isValidNumber(params[PARAMS.SHOP_ID]) || !getState().hasIn(['shops', 'byId', params[PARAMS.SHOP_ID]])) {
          const current = getState().getIn(['shops', 'current']);

          if (current) {
            params[PARAMS.SHOP_ID] = getState().getIn(['shops', 'current']);
          }
        }

        break;
      }

      default: {
        break;
      }
    }

    let route = path
      ? generatePath(path, params)
      : PAGE_TO_ROUTE_MAP.hasOwnProperty(page)
        ? generatePath(PAGE_TO_ROUTE_MAP[page], params)
        : undefined;

    if (route && getSize(search) > 0) {
      route += `?${createSearchParams(search)}`;
    }

    return route;
  }

  function canNavigateTo({ page, route }) {
    if (!route || route === history.location.pathname) return false;

    switch (page) {
      case PAGES.SCHEDULE: {
        if (PAGE === PAGES.SCHEDULE_LISTINGS || PAGE === PAGES.SCHEDULE_UPDATES) {
          return false;
        }

        break;
      }

      case PAGES.STUDIO: {
        if (PAGE === PAGES.STUDIO_PHOTOS || PAGE === PAGES.STUDIO_VIDEOS) {
          return false;
        }

        break;
      }

      default: {
        break;
      }
    }

    const shopId = getState().getIn(['shops', 'current']);
    const syncStatus = getState().getIn(['shops', 'byId', shopId, 'syncStatus']);
    const subscription = getState().getIn(['user', 'subscriptions', shopId]) || EMPTY.MAP;

    if (
      isUnpaid(subscription) ||
      isBillingError({ plan: subscription.get('plan') || PLAN.ERROR, syncStatus }) ||
      isInvalid(syncStatus) ||
      isImporting(syncStatus) || (
        syncStatus === STATUSES.TOKEN_REJECTED ||
        syncStatus === STATUSES.INCOMPLETE_IN_VACATION_MODE ||
        (
          syncStatus === STATUSES.QUOTA_EXCEEDED &&
          !getState().getIn(['shops', 'hideRateLimitPage', shopId])
        )
      )
    ) {
      return true;
    }

    switch (PAGE) {
      case PAGES.BULK_EDIT: {
        dispatch(actions.bulkEdit.closeBulkEdit({ route }));
        return false;
      }

      case PAGES.PROFILES: {
        if (getSize(getState().getIn(['profiles', 'editing']))) {
          dispatch(actions.profiles.setModal({ type: MODALS.PROFILES.LEAVE, route }));
          return false;
        }

        return true;
      }

      case PAGES.EDIT: {
        if (isProductEdited(getState().getIn(['edit', 'products'], EMPTY.MAP))) {
          dispatch(actions.edit.setModal({ type: MODALS.CONFIRMATIONS.CANCEL, route }));
          return false;
        }

        return true;
      }

      default: {
        return true;
      }
    }
  }

  function goTo({ page, params = {}, search = {}}) {
    let path;

    switch (page) {
      case PAGES.LISTINGS: {
        path = PAGE_TO_ROUTE_MAP[PAGES.LISTINGS];

        if (!params[PARAMS.STATUS]) {
          params[PARAMS.STATUS] = (
            getState().getIn(['listings', 'status']) ||
            matchPath({ path, end: true }, history.location.pathname)?.params?.[PARAMS.STATUS] ||
            getPublishStatus({
              action: PUBLISH_ACTION.PUBLISH,
              channel: getState().getIn(['shops', 'byId', getState().getIn(['shops', 'current']), 'channel']),
            })
          );
        }

        break;
      }

      case PAGES.PROFILES: {
        path = PAGE_TO_ROUTE_MAP[PAGES.PROFILES];

        if (!params[PARAMS.TYPE]) {
          params[PARAMS.TYPE] = (
            matchPath({ path, end: true }, history.location.pathname)?.params?.[PARAMS.TYPE] ||
            PROFILE_TYPE.STANDARD
          );
        }

        if (
          params[PARAMS.TYPE] === PROFILE_TYPE.STANDARD ||
          !getState().getIn(['user', 'features', FEATURE.AI_PROFILES])
        ) {
          delete params[PARAMS.TYPE];
        }

        break;
      }

      case PAGES.SCHEDULE: {
        path = PAGE_TO_ROUTE_MAP[PAGES.SCHEDULE_LISTINGS];
        break;
      }

      case PAGES.STUDIO: {
        const videoStudioEnabled = getState().getIn(['user', 'features', FEATURE.VIDEO_STUDIO]);

        if (videoStudioEnabled) {
          path = PAGE_TO_ROUTE_MAP[PAGES.STUDIO_PHOTOS];
        }

        break;
      }

      default: {
        break;
      }
    }

    const route = makeRoute({ page, params, path, search });

    if (canNavigateTo({ page, route })) {
      navigateTo(route);
    }
  }

  function goToShop(shopId) {
    let page = PAGE;

    if (
      page !== PAGES.LISTINGS &&
      page !== PAGES.PROFILES &&
      page !== PAGES.SCHEDULE_LISTINGS &&
      page !== PAGES.SCHEDULE_UPDATES &&
      page !== PAGES.STUDIO && // TODO: delete with FEATURE_FLAG_VIDEO_STUDIO
      page !== PAGES.STUDIO_PHOTOS &&
      page !== PAGES.STUDIO_VIDEOS
    ) {
      page = PAGES.LISTINGS;
    }

    goTo({ page, params: { [PARAMS.SHOP_ID]: shopId }});
  }

  return {
    goTo,
    goToShop,
    makeRoute,
    PAGE,
  };
}
